import { FormControl } from '@angular/forms';
import { HttpParams } from '@angular/common/http';

export type FormControlFields<T> = {
    [Prop in keyof T]: FormControl<T[Prop] | null>
}

export type Mapper<TIn extends object, TOut extends object> = (input: TIn) => TOut

export type FieldMappingTransformer<TIn, TOut> = (value: TIn) => TOut;

export type ComputedFieldMappingTransformer<TIn, TOut> = (input: Pick<TIn, keyof TIn>) => TOut

/**
 * Definición de diccionario de funciones transformadoras para serializar/deserializar cada campo <br/>
 * `[campo de destino]: (valor de origen) => valor de destino`
 */
export type FieldMappings<TIn extends object, TOut extends object> = {
    [Prop in keyof TIn & keyof TOut]?: FieldMappingTransformer<TIn[Prop], TOut[Prop]>
}

export type ComputedFieldMappings<TIn extends object, TOut extends object> = {
    [Prop in keyof TOut]?: ComputedFieldMappingTransformer<TIn, TOut[Prop]>
};


/**
 * Modo de aplicación del mapeo, siendo completo o parcial. Si es parcial solo se aplica la definición
 */
type ApplyingMappingsMode =
    | 'full'
    | 'partial'
    ;


export interface BuildMapperOptions<TIn extends object, TOut extends object> {
    mappings?: FieldMappings<TIn, TOut>,
    computed?: ComputedFieldMappings<TIn, TOut>,
    mode?: ApplyingMappingsMode
}


export function typeGuard<T>(value: any, key: keyof T): value is T {
    return (key in value);
}

/**
 * TypeGuard para verificar si es un falsy y/o un objeto o estructura vacía.
 * @param value
 */
export function isNullOrEmpty(value: any): value is void | undefined | null | never {
    if (typeof value === 'undefined' || value === null) return true;
    if (typeof value === 'string' && value.length === 0) return true;
    if (typeof value === 'object') {
        if ('length' in value) return (value.length === 0);
        if ('size' in value) return (value.size === 0);
        if (Object.getPrototypeOf(value) === Object.prototype) return (Object.keys(value).length === 0);
    }
    return false;
}


export function from<TIn extends object>() {
    return {
        take: <K extends keyof TIn>(...fields: ReadonlyArray<K>) => {
            return {
                map: <TOut>(fn: (value: Pick<TIn, typeof fields[number]>) => TOut) => fn,
            };
        },
    };
}


/**
 * Retorna una función, que a partir de una definición, puede mapear un tipo de dato a otro.
 */
export function buildMapper<TIn extends object, TOut extends object>(options: BuildMapperOptions<TIn, TOut>): Mapper<TIn, TOut> {
    const {
        mappings = {} as FieldMappings<TIn, TOut>,
        computed = {} as ComputedFieldMappings<TIn, TOut>,
        mode = 'full' as ApplyingMappingsMode,
    } = options;

    return (input: TIn) => {
        const output = {} as Partial<TOut>;

        if (mode === 'full') {
            Object.entries(input).forEach(([prop, value]) => {
                if (!(prop in mappings) && !(prop in computed)) {
                    output[prop as keyof TOut] = value as TOut[keyof TOut];
                }
            });
        }

        Object.entries(mappings).forEach(([prop, mapFn]) => {
            output[prop as keyof TOut] = (mapFn as FieldMappingTransformer<TIn[keyof TIn], TOut[keyof TOut]>)(input[prop as keyof TIn]);
        });

        Object.entries(computed).forEach(([prop, mapFn]) => {
            output[prop as keyof TOut] = (mapFn as ComputedFieldMappingTransformer<TIn, TOut[keyof TOut]>)(input);
        });

        return output as TOut;
    };
}

export function getFromPath<T>(obj: object, path: string): T | undefined {
    const keys = path.split('.');
    let current: any = obj;

    for (const key of keys) {
        if (!isNullOrEmpty(current) && Object.prototype.hasOwnProperty.call(current, key)) {
            current = current[key];
        } else {
            return undefined;
        }
    }

    return current as T;
}

export function toHttpParams<T extends object>(options: T): HttpParams {
    let params = new HttpParams();
    Object.entries(options).forEach(([key, value]) => {
        if (!isNullOrEmpty(value)) {
            params = params.set(key, typeof value === 'object' ? JSON.stringify(value) : value);
        }
    });
    return params;
}
